import { Box, SimpleGrid, Flex } from '@chakra-ui/react';

import Select from '@components/filter/select';
import InputComp from '@components/formField/InputComp';
import { debounce, isEmpty, isEqual } from 'lodash';
import { useFilters } from '../../hooks/state/useFilters';
import { useGetCity, useGetDistrict, useGetProvince, useGetVillage } from '../../hooks/query/useGetLocation';
import { CoordinateComp } from './CoordinateComp';
import TextareaInput from '@components/formField/TextareaInput';
import { initialLocationState, useCustomerDetail } from '@pages/Parahub/hooks/state/useCustomerDetail';
import { WarningIcon } from '@assets/icons';

export default function CustomerLocation() {
    const filters = useFilters();
    const { formData, updateFields } = useCustomerDetail();

    const dataProvince = useGetProvince(filters.provinceSearch);
    const dataCity = useGetCity(formData?.province_id, filters.citySearch);
    const dataDistrict = useGetDistrict(formData?.city_id, filters.districtSearch);
    const dataVillage = useGetVillage(formData?.district_id, filters.villageSearch);

    const selects = [
        {
            key_name: 'province',
            key_id: 'province_id',
            title: 'Provinsi',
            placeholder: 'Pilih provinsi',
            value: { id: formData?.province_id, name: formData?.province },
            data: dataProvince?.data?.listProvince,
            isLoading: dataProvince?.isLoading,
            refetch: dataProvince?.refetch,
            isDisabled: false,
            onSearch: debounce((query) => filters.set({ provinceSearch: query }), 500),
            isInvalid: isEmpty(dataProvince?.data?.listProvince),
        },
        {
            key_name: 'city',
            key_id: 'city_id',
            title: 'Kota/Kabupaten',
            placeholder: 'Pilih kota/kabupaten',
            isDisabled: isEqual(formData?.province, initialLocationState?.province),
            value: { id: formData?.city_id, name: formData?.city },
            data: dataCity?.data?.listCity,
            isLoading: dataCity?.isLoading,
            refetch: dataCity?.refetch,
            onSearch: debounce((query) => filters.set({ citySearch: query }), 500),
            isInvalid: isEmpty(dataProvince?.data?.listCity),
        },
        {
            key_name: 'district',
            key_id: 'district_id',
            title: 'Kecamatan',
            placeholder: 'Pilih kecamatan',
            isDisabled: isEqual(formData?.city, initialLocationState?.city),
            value: { id: formData?.district_id, name: formData?.district },
            data: dataDistrict?.data?.listDistrict,
            isLoading: dataDistrict?.isLoading,
            refetch: dataDistrict?.refetch,
            onSearch: debounce((query) => filters.set({ districtSearch: query }), 500),
            isInvalid: isEmpty(dataProvince?.data?.listDistrict),
        },
        {
            key_name: 'subdistrict',
            key_id: 'subdistrict_id',
            title: 'Kelurahan',
            placeholder: 'Pilih kelurahan',
            isDisabled: isEqual(formData?.district, initialLocationState?.district),
            value: { id: formData?.subdistrict_id, name: formData?.subdistrict },
            data: dataVillage?.data?.listVillage,
            isLoading: dataVillage?.isLoading,
            refetch: dataVillage?.refetch,
            onSearch: debounce((query) => filters.set({ villageSearch: query }), 500),
            isInvalid: isEmpty(dataProvince?.data?.listVillage),
        },
        {
            key_name: 'zip_code',
            title: 'Kode Pos',
            placeholder: 'Kode Pos',
            isDisabled: isEqual(formData?.zip_code, initialLocationState?.zip_code),
            value: formData?.zip_code,
        },
    ];

    return (
        <Box>
            <SimpleGrid columns={3} gap="24px" mb="24px">
                <CoordinateComp label="Koordinat" values={formData} onChange={updateFields} />

                {selects.map(
                    ({ key_id, key_name, title, placeholder, onSearch, data, isLoading, refetch, isDisabled }, idx) => {
                        const isWarning = !isEmpty(formData)
                            ? isEqual(initialLocationState[key_name], formData[key_name])
                            : false;
                        const label = isWarning ? (
                            <Flex justifyContent={'space-between'} alignItems={'center'}>
                                {title}
                                <WarningIcon />
                            </Flex>
                        ) : (
                            title
                        );
                        return idx === 4 ? (
                            <InputComp
                                label={label}
                                placeholder={placeholder}
                                value={formData?.zip_code}
                                isDisabled={isDisabled}
                                isReadOnly
                            />
                        ) : (
                            <Select
                                title={label}
                                placeholder={placeholder}
                                selected={{
                                    id: !isEmpty(formData) ? formData[key_id] : '',
                                    name: !isEmpty(formData) ? formData[key_name] : '',
                                }}
                                data={data}
                                refetchData={refetch}
                                isLoading={isLoading}
                                isDisabled={isDisabled}
                                isMultiple={false}
                                onSearch={onSearch}
                                onChangeFilter={(value) => {
                                    if (key_name === 'subdistrict') {
                                        const villageObj = data.find((v) => v.id === value.id);
                                        console.log('villageObj: ', villageObj);

                                        updateFields({
                                            subdistrict_id: value.id,
                                            subdistrict: value.name,
                                            zip_code: villageObj?.zip || '',
                                        });
                                    } else {
                                        updateFields({
                                            [key_id]: value.id,
                                            [key_name]: value.name,
                                        });
                                    }
                                }}
                                onCloseOptions={() => filters.reset()}
                            />
                        );
                    }
                )}
            </SimpleGrid>

            <TextareaInput
                label="Alamat Lengkap"
                placeholder="Alamat lengkap customer parahub ..."
                value={formData?.address}
                onChange={(e) => {
                    updateFields({
                        address: e.target.value,
                    });
                }}
            />
        </Box>
    );
}
